/** The figures the Budget summary card shows, worked out from the period's usage. */

import type { Budget, Usage } from '@/api/types';
import { currentMonth, monthLabel } from '@/ui/format';

import { isCurrentPeriod, isYear } from './usageUi';

export type BudgetState = 'ok' | 'warn' | 'over';

export const STATE_BADGE: Record<BudgetState, string> = { ok: 'badge-success', warn: 'badge-warning', over: 'badge-danger' };

function budgetState(budget: Budget): BudgetState {
  if (budget.used_pct >= 100) return 'over';
  return budget.used_pct >= budget.alert_threshold_pct ? 'warn' : 'ok';
}

function stateLabel(state: BudgetState, budget: Budget): string {
  return { ok: 'Within budget', warn: `Above ${budget.alert_threshold_pct}% alert`, over: 'Cap reached' }[state];
}

/**
 * How far through the budget's span today is, 0–1. A year's cap covers its
 * months so far, so the span is those months; only for the current period.
 */
function elapsed(period: string): number | null {
  if (!isCurrentPeriod(period)) return null;
  const now = new Date();
  const days = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const intoMonth = (now.getDate() - 1 + (now.getHours() * 60 + now.getMinutes()) / 1440) / days;
  if (!isYear(period)) return intoMonth;
  const months = now.getMonth() + 1;
  return (months - 1 + intoMonth) / months;
}

/** A year's cap is its months' caps so far, added up. */
function monthsCounted(period: string): number {
  if (!isYear(period)) return 1;
  return currentMonth().startsWith(period) ? Number(currentMonth().slice(5)) : 12;
}

function capNote(budget: Budget, months: number): string {
  return [
    months > 1 && `${months} months`,
    budget.hard_stop ? 'hard stop at cap' : 'soft cap, warns only',
    budget.inherited_from && `inherited from ${monthLabel(budget.inherited_from)}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Only a month projects: a year's cap still grows month by month. */
function projection(period: string, spent: number, pace: number | null): number | null {
  if (pace === null || pace <= 0.03 || isYear(period)) return null;
  return spent / pace;
}

export function budgetFigures(data: Usage) {
  const { budget } = data;
  const state = budgetState(budget);
  const cap = Number(budget.limit_usd);
  const spent = Number(budget.committed_usd);
  const inFlight = Number(budget.reserved_usd);
  const pace = elapsed(data.month);
  return {
    budget,
    state,
    status: stateLabel(state, budget),
    cap,
    spent,
    inFlight,
    remaining: Math.max(Number(budget.remaining_usd), 0),
    // Over the cap the bar's whole is what was used, so the parts still add up.
    whole: Math.max(cap, spent + inFlight),
    paceUsd: pace !== null ? cap * pace : null,
    projected: projection(data.month, spent, pace),
    capNote: capNote(budget, monthsCounted(data.month)),
  };
}

export type BudgetFigures = ReturnType<typeof budgetFigures>;
